import React, { useState, useEffect, useCallback, SyntheticEvent, useMemo } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import { useNavigate } from 'react-router-dom';
import axios from "axios";
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import {
  ConfirmDialog,
  DataTable,
  DateField,
  EmptyState,
  MoneyField,
  Notice,
  PageHeader,
  StatCard,
  useNotify,
  type DataTableColumn,
} from '@routes-plan/ui';

import { Office } from '../../../models/office';
import { Route } from '../../../models/route';
import { useSelection } from '../../../components/context/SelectionContext';
import RouteExpensesModal from './modal/RouteExpensesModal';
import { companyCurrency, companyLocale, formatCurrency } from '../../../utils/format/currency';
import ConfirmDayClosingModal from './modal/ConfirmDayClosingModal';
import moment from 'moment-timezone';
import PhotoViewerButton from '../../../components/attachments/PhotoViewerButton';
import HeaderResponsive from '../../../components/layouts/HeaderResponsive';
import PermissionButton from '../../../components/common/PermissionButton';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { operationalTimezone } from '../../../utils/operationalDateTime';
import { useT } from '../../../i18n';
import { setPageTitle } from '../../../utils/pageTitle';
import { FilterBar, SelectField, Section } from '../components/ProcessFilters';

/** Compatible con navegadores sin Promise.allSettled (p. ej. Safari antes de 13, Chrome antes de 76). */
function promiseAllSettled<T>(
  promises: Array<PromiseLike<T>>,
): Promise<Array<{ status: 'fulfilled'; value: T } | { status: 'rejected'; reason: unknown }>> {
  return Promise.all(
    promises.map((p) =>
      Promise.resolve(p).then(
        (value) => ({ status: 'fulfilled' as const, value }),
        (reason) => ({ status: 'rejected' as const, reason }),
      ),
    ),
  );
}

interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}
interface ExpenseItem {
  id: number;
  date: string;
  description: string;
  value: number;
  balance: number;
  expenseType: {
    id: number;
    name: string;
    cashBoxAdjust: boolean;
    operationType?: {
      id: number;
      name: string;
    };
  };
}
interface CreatedExpenseDisplayItem {
  id: number;
  expenseTypeId: number;
  value: number;
  description: string;
  name: string;
  cashBoxId: number;
}
interface InsuranceBoxSummary {
  cashBoxId: number | null;
  currentBalance: number;
  dateIncome: number;
  dateOut: number;
  dateNet: number;
  loanCount: number;
}

/** True si el cierre y la lista de gastos indican que ese día no hubo actividad operativa. */
function liquidationDayHasNoMovements(
  dayClosing: Record<string, unknown> | null,
  expensesPayload: unknown,
): boolean {
  const tuple = expensesPayload as [ExpenseItem[], number] | undefined;
  const rawExpenseList = tuple?.[0];
  const expenseList = Array.isArray(rawExpenseList) ? rawExpenseList : [];
  const hasExpenseRows = expenseList.length > 0;

  const n = (v: unknown) => {
    const x = Number(v);
    return Number.isFinite(x) ? x : 0;
  };

  if (!dayClosing) {
    return !hasExpenseRows;
  }

  const hasDayClosingActivity =
    n(dayClosing.paymentCount) > 0 ||
    n(dayClosing.loanCount) > 0 ||
    n(dayClosing.paymentTotal) > 0 ||
    n(dayClosing.loanTotal) > 0 ||
    n(dayClosing.expenseTotal) > 0 ||
    n(dayClosing.otherIncomeTotal) > 0 ||
    n(dayClosing.otherExpenseTotal) > 0 ||
    n(dayClosing.discountTotal) > 0 ||
    n(dayClosing.refinanceValue) > 0 ||
    n(dayClosing.refinanceTotal) > 0 ||
    n(dayClosing.interestTotal) > 0 ||
    n(dayClosing.insuranceChargeTotal) > 0;

  return !hasExpenseRows && !hasDayClosingActivity;
}

const RouteSettlement: React.FC<ShowAlerts> = ({ showLoading, hideLoading }) => {
  const t = useT();
  const notify = useNotify();
  const showAlert = (message: string, severity: 'success' | 'info' | 'warning' | 'error' = 'info') => notify[severity](message);
  useEffect(() => {
    setPageTitle('routeSettlement.title');
  }, []);
  const navigate = useNavigate();
  const { canCreate, canUpdate, canDelete, canRegisterExpenses } =
    useResourcePermissions('/processes/route-settlement');

  // Estados
  const [officeRoutes, setOfficeRoutes] = useState<Route[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const [cashBoxId, setCashBoxId] = useState<number>(0);
  const [visitOrderId, setVisitOrderId] = useState<number | null>(null);
  const [otherIncomeTotal, setOtherIncomeTotal] = useState<number>(0);
  const [otherExpenseTotal, setOtherExpenseTotal] = useState<number>(0);
  const [interestTotal, setInterestTotal] = useState<number>(0);
  const [insuranceChargeTotal, setInsuranceChargeTotal] = useState<number>(0);
  const [insuranceBoxSummary, setInsuranceBoxSummary] = useState<InsuranceBoxSummary | null>(null);
  const [refinanceTotal, setRefinanceTotal] = useState<number>(0);
  const [refinanceValue, setRefinanceValue] = useState<number>(0);
  const [cashBoxValue, setCashBoxValue] = useState<number>(0);
  const [paymentTotal, setPaymentTotal] = useState(0);
  const [paymentCount, setPaymentCount] = useState(0);
  const [loanTotal, setLoanTotal] = useState(0);
  const [loanCount, setLoanCount] = useState(0);
  const [valuePaymentCancel, setValuePaymentCancel] = useState(0);
  const [cashDifference, setCashDifference] = useState(0);
  const [discountTotal, setDiscountTotal] = useState(0);
  const [valueLoanCancel, setValueLoanCancel] = useState(0);
  const [countLoanCancel, setCountLoanCancel] = useState(0);
  const [clientCount, setClientCount] = useState(0);
  const [valueClientDefault, setValueClientDefault] = useState(0);
  const [countClientDefault, setCountClientDefault] = useState(0);
  const [routePercentage, setRoutePercentage] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalOpenConfirm, setModalConfirm] = useState(false);
  const [listExpenses, setListExpenses] = useState<[ExpenseItem[], number] | null>(null);
  const [deletingExpenseId, setDeletingExpenseId] = useState<number | null>(null);
  const [base, setBase] = useState<string>("0");
  const [cash, setCash] = useState<string>("0");
  const [activeSearch, setActiveSearch] = useState<{ routeId: string | number; date: string } | null>(null);
  const [reliquidating, setReliquidating] = useState(false);
  const [isResolvingCashBox, setIsResolvingCashBox] = useState(false);
  const [confirmRecalculate, setConfirmRecalculate] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState<{ id: number; name: string; date?: string } | null>(null);
  const [dateWithoutMovements, setDateWithoutMovements] = useState(false);
  const {
    office_id: officeId,
    setOffice_id: setOfficeId,
    collect_id: routeId,
    setCollect_id: setRouteId,
    user_id: userId,
    setUser_id: setUserId,
  } = useSelection();
  const [currentDate, setCurrentDate] = useState(() => {
    return moment().tz(operationalTimezone()).format("YYYY-MM-DD"); // valor inicial: hoy
  });
  const [timeDate] = useState(() => {
    return moment().tz(operationalTimezone()).format("HH:mm:ss");
  });
  const normalizeExpenseDate = (rawDate: string) => {
    return moment.tz(rawDate, operationalTimezone()).format("YYYY-MM-DD");
  };
  const parseLocalizedNumber = (value: unknown): number | null => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;

    const raw = String(value).trim();
    if (!raw) return null;

    const normalized = raw
      .replace(/\s+/g, '')
      .replace(/\$/g, '')
      .replace(/[^0-9,.\-]/g, '');

    if (!normalized || normalized === '-' || normalized === ',' || normalized === '.') {
      return null;
    }

    const esFormat = /^-?\d{1,3}(\.\d{3})+(,\d+)?$/;
    const enFormat = /^-?\d{1,3}(,\d{3})+(\.\d+)?$/;
    const decimalComma = /^-?\d+,\d+$/;
    const decimalDot = /^-?\d+(\.\d+)?$/;

    if (esFormat.test(normalized)) {
      const parsed = Number(normalized.replace(/\./g, '').replace(',', '.'));
      return Number.isFinite(parsed) ? parsed : null;
    }
    if (enFormat.test(normalized)) {
      const parsed = Number(normalized.replace(/,/g, ''));
      return Number.isFinite(parsed) ? parsed : null;
    }
    if (decimalComma.test(normalized)) {
      const parsed = Number(normalized.replace(',', '.'));
      return Number.isFinite(parsed) ? parsed : null;
    }
    if (decimalDot.test(normalized)) {
      const parsed = Number(normalized);
      return Number.isFinite(parsed) ? parsed : null;
    }

    const sign = normalized.startsWith('-') ? -1 : 1;
    const digits = normalized.replace(/\D/g, '');
    if (!digits) return null;

    const parsed = Number(digits);
    return Number.isFinite(parsed) ? sign * parsed : null;
  };
  const baseNumber = useMemo(() => parseLocalizedNumber(base) ?? 0, [base]);
  const cashNumber = useMemo(() => parseLocalizedNumber(cash) ?? 0, [cash]);
  const canSearch = Boolean(officeId) && Boolean(routeId);
  const canOperateWithCashBox = canSearch && cashBoxId > 0 && !isResolvingCashBox;
  const activeRouteId = activeSearch?.routeId;
  const activeDate = activeSearch?.date;
  const hasActiveSearch = Boolean(activeSearch);
  // Efectos
  useEffect(() => {

    const fetchData = async () => {
      showLoading();
      try {
        const [officesResponse, officeRoutesResponse] = await Promise.all([
          axios.get('offices'),
          officeId ? axios.get(`offices/${officeId}/routes`) : Promise.resolve(null)
        ]);

        const officeList = officesResponse.data?.data ?? officesResponse.data ?? [];
        const officesOk = Array.isArray(officeList) ? officeList : [];
        setOffices(officesOk);
        if (officesOk.length === 0) {
          showAlert(t('routeSettlement.noOffices'), 'warning');
        }

        const [{ data: userData }] = await Promise.all([axios.get('user'),]);
        setUserId(userData.data.id);

        if (officeRoutesResponse) {
          const rawRoutes =
            officeRoutesResponse.data?.data ?? officeRoutesResponse.data ?? [];
          const routesOk = Array.isArray(rawRoutes) ? rawRoutes : [];
          setOfficeRoutes(routesOk);
          if (officesOk.length > 0 && officeId && routesOk.length === 0) {
            showAlert(t('routeSettlement.noRoutes'), 'warning');
            setRouteId('');
          }
        } else {
          setOfficeRoutes([]);
        }
      } catch (error) {
        showAlert(t('routeSettlement.loadOfficesError'), 'error');
      } finally {
        hideLoading();
      }
    };

    fetchData();
  }, [officeId]);

  const resetSearchData = () => {
    setCashBoxId(0);
    setVisitOrderId(null);
    setOtherIncomeTotal(0);
    setOtherExpenseTotal(0);
    setInterestTotal(0);
    setInsuranceChargeTotal(0);
    setInsuranceBoxSummary(null);
    setRefinanceTotal(0);
    setRefinanceValue(0);
    setCashBoxValue(0);
    setPaymentTotal(0);
    setPaymentCount(0);
    setLoanTotal(0);
    setLoanCount(0);
    setValuePaymentCancel(0);
    setDiscountTotal(0);
    setValueLoanCancel(0);
    setCountLoanCancel(0);
    setClientCount(0);
    setValueClientDefault(0);
    setCountClientDefault(0);
    setRoutePercentage(0);
    setListExpenses(null);
    setBase("0");
    setCash("0");
    setCashDifference(0);
    setDateWithoutMovements(false);
  };

  const applyDayClosingData = (
    dayClosingRecord: any | null,
    options?: { preserveManualFields?: boolean },
  ) => {
    const preserveManualFields = Boolean(options?.preserveManualFields);

    if (!dayClosingRecord) {
      setOtherIncomeTotal(0);
      setOtherExpenseTotal(0);
      setInterestTotal(0);
      setInsuranceChargeTotal(0);
      setRefinanceTotal(0);
      setRefinanceValue(0);
      setCashBoxValue(0);
      setPaymentTotal(0);
      setPaymentCount(0);
      setLoanTotal(0);
      setLoanCount(0);
      setValuePaymentCancel(0);
      setDiscountTotal(0);
      setValueLoanCancel(0);
      setCountLoanCancel(0);
      setClientCount(0);
      setValueClientDefault(0);
      setCountClientDefault(0);
      setRoutePercentage(0);
      if (!preserveManualFields) {
        setBase("0");
        setCash("0");
        setCashDifference(0);
      }
      return;
    }

    if (!preserveManualFields) {
      setBase(String(dayClosingRecord.base ?? 0));
      setCash(String(dayClosingRecord.cash ?? 0));
      setCashDifference(Number(dayClosingRecord.cashDifference ?? 0));
    }
    setOtherIncomeTotal(Number(dayClosingRecord.otherIncomeTotal ?? 0));
    setOtherExpenseTotal(Number(dayClosingRecord.otherExpenseTotal ?? 0));
    setInterestTotal(Number(dayClosingRecord.interestTotal ?? 0));
    setInsuranceChargeTotal(Number(dayClosingRecord.insuranceChargeTotal ?? 0));
    setRefinanceTotal(Number(dayClosingRecord.refinanceTotal ?? 0));
    setRefinanceValue(Number(dayClosingRecord.refinanceValue ?? 0));
    setCashBoxValue(Number(dayClosingRecord.cashBoxValue ?? 0));
    setPaymentTotal(Number(dayClosingRecord.paymentTotal ?? 0));
    setPaymentCount(Number(dayClosingRecord.paymentCount ?? 0));
    setLoanTotal(Number(dayClosingRecord.loanTotal ?? 0));
    setLoanCount(Number(dayClosingRecord.loanCount ?? 0));
    setValuePaymentCancel(Number(dayClosingRecord.cancellationTotal ?? 0));
    setDiscountTotal(Number(dayClosingRecord.discountTotal ?? 0));
    setValueLoanCancel(Number(dayClosingRecord.cancellationTotal ?? 0));
    setCountLoanCancel(Number(dayClosingRecord.cancellationCount ?? 0));
    setClientCount(Number(dayClosingRecord.clientCount ?? 0));
    setValueClientDefault(Number(dayClosingRecord.defaultClientValue ?? 0));
    setCountClientDefault(Number(dayClosingRecord.defaultClientCount ?? 0));
    setRoutePercentage(Number(dayClosingRecord.routePercentage ?? 0));
  };

  const loadDayClosingByDate = async ({
    routeId,
    cashBoxId,
    date,
  }: {
    routeId: string | number;
    cashBoxId: number;
    date: string;
  }) => {
    if (!cashBoxId) {
      return null;
    }

    const normalizedDate = normalizeExpenseDate(date);
    const { data } = await axios.post('/day-closings/search', {
      routeId: Number(routeId),
      cashBoxId,
      date: normalizedDate,
    });

    return data?.data || null;
  };

  const ensureCashBoxForRoute = async (routeId: string | number): Promise<number> => {
    const parsedRouteId = Number(routeId);
    if (!Number.isInteger(parsedRouteId) || parsedRouteId <= 0) {
      return 0;
    }

    setIsResolvingCashBox(true);
    try {
      const cashBoxResponse = await axios.get(`/routes/${parsedRouteId}/cash-box`);
      const cashBoxes = cashBoxResponse.data?.data ?? cashBoxResponse.data;
      const firstCashBox = Array.isArray(cashBoxes) ? cashBoxes[0] : null;
      const resolvedCashBoxId = Number(firstCashBox?.id || 0);
      setCashBoxId(resolvedCashBoxId);
      return resolvedCashBoxId;
    } catch (error) {
      console.error('Error resolviendo cashBoxId:', error);
      setCashBoxId(0);
      return 0;
    } finally {
      setIsResolvingCashBox(false);
    }
  };

  const fetchSearchData = async (
    params: { routeId: string | number; date: string },
    attempt = 0,
  ): Promise<void> => {
    showLoading();
    setDateWithoutMovements(false);
    setListExpenses(null);
    try {
      const requestDate = normalizeExpenseDate(params.date);
      const cashBoxResponse = await axios.get(`/routes/${params.routeId}/cash-box`);
      const cashBoxes = cashBoxResponse.data?.data ?? cashBoxResponse.data;
      const firstCashBox = Array.isArray(cashBoxes) ? cashBoxes[0] : null;
      const newCashBoxId = Number(firstCashBox?.id || 0);
      setCashBoxId(newCashBoxId);

      if (!newCashBoxId) {
        applyDayClosingData(null);
        setListExpenses([[], 0]);
        setVisitOrderId(null);
        setInsuranceBoxSummary(null);
        showAlert(t('routeSettlement.noCashBox'), 'error');
        return;
      }

      const [dayClosingResponse, expensesResponse, visitOrdersResponse, insuranceBoxResponse] = await promiseAllSettled([
        loadDayClosingByDate({
          routeId: params.routeId,
          cashBoxId: newCashBoxId,
          date: requestDate,
        }),
        axios.post(`/cash-box-expenses/search`, {
          routeId: params.routeId,
          date: requestDate,
        }),
        axios.get(`/routes/${params.routeId}/visit-orders`),
        axios.post(`/routes/${params.routeId}/insurance-box/summary`, {
          date: requestDate,
        }),
      ]);

      if (dayClosingResponse.status === 'fulfilled') {
        applyDayClosingData(dayClosingResponse.value);
        if (dayClosingResponse.value === null) {
          showAlert(t('routeSettlement.noClosingYet'), 'info');
        }
      } else {
        console.error('Error cargando closer:', dayClosingResponse.reason);
        applyDayClosingData(null);
        showAlert(t('routeSettlement.closingLoadError'), 'warning');
      }

      if (expensesResponse.status === 'fulfilled') {
        setListExpenses(expensesResponse.value.data);
      } else {
        console.error('Error cargando gastos:', expensesResponse.reason);
        setListExpenses(null);
        showAlert(t('routeSettlement.expensesLoadError'), 'warning');
      }

      if (visitOrdersResponse.status === 'fulfilled') {
        const activeVisitOrder = Array.isArray(visitOrdersResponse.value.data?.data)
          ? visitOrdersResponse.value.data.data[0]
          : null;
        setVisitOrderId(activeVisitOrder?.id ? Number(activeVisitOrder.id) : null);
      } else {
        console.error('Error cargando ruta activa:', visitOrdersResponse.reason);
        setVisitOrderId(null);
      }

      if (insuranceBoxResponse.status === 'fulfilled') {
        const summary = insuranceBoxResponse.value.data?.data || null;
        setInsuranceBoxSummary(summary);
      } else {
        console.error('Error cargando caja seguro:', insuranceBoxResponse.reason);
        setInsuranceBoxSummary(null);
        showAlert(t('routeSettlement.insuranceLoadError'), 'warning');
      }

      if (dayClosingResponse.status === 'fulfilled' && expensesResponse.status === 'fulfilled') {
        setDateWithoutMovements(
          liquidationDayHasNoMovements(dayClosingResponse.value, expensesResponse.value.data),
        );
      } else {
        setDateWithoutMovements(false);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      if (attempt < 1) {
        await new Promise((r) => setTimeout(r, 600));
        hideLoading();
        return fetchSearchData(params, attempt + 1);
      }
      showAlert(t('routeSettlement.loadError'), 'error');
      resetSearchData();
    } finally {
      hideLoading();
    }
  };

  const mergeLiquidationSearchTarget = useCallback((routeId: string | number, date: string) => {
    setActiveSearch((prev) => {
      const next = { routeId, date };
      if (
        prev &&
        String(prev.routeId) === String(next.routeId) &&
        prev.date === next.date
      ) {
        return prev;
      }
      return next;
    });
  }, []);

  useEffect(() => {
    if (!activeSearch) return;
    fetchSearchData(activeSearch);
  }, [activeSearch]);

  useEffect(() => {
    if (!officeId || !routeId) {
      setActiveSearch(null);
      resetSearchData();
      return;
    }

    if (isResolvingCashBox) {
      return;
    }

    mergeLiquidationSearchTarget(routeId, currentDate);
  }, [officeId, routeId, currentDate, isResolvingCashBox, mergeLiquidationSearchTarget]);

  // Handlers
  const handleReliquidar = async () => {
    if (!canUpdate) {
      showAlert(t('routeSettlement.noPermissionRecalculate'), 'warning');
      return;
    }
    if (!canSearch) {
      showAlert(t('routeSettlement.selectOfficeAndRoute'), 'warning');
      return;
    }
    if (!cashBoxId || isResolvingCashBox) {
      showAlert(t('routeSettlement.cashBoxNotReady'), 'warning');
      return;
    }
    const parsedRouteId = Number(routeId);
    if (!Number.isInteger(parsedRouteId) || parsedRouteId <= 0) {
      showAlert(t('routeSettlement.selectValidRoute'), 'warning');
      return;
    }
    setConfirmRecalculate(true);
  };
  const runReliquidar = async () => {
    setConfirmRecalculate(false);
    const parsedRouteId = Number(routeId);
    const dateStr = normalizeExpenseDate(currentDate);
    setReliquidating(true);
    showLoading();
    try {
      await axios.post('/day-closings/reliquidate', {
        date: dateStr,
        routeId: parsedRouteId,
      });
      showAlert(t('routeSettlement.recalculatedOk'), 'success');
      await fetchSearchData({ routeId: routeId, date: currentDate });
    } catch (error: any) {
      const backendMessage = error?.response?.data?.message || error?.response?.data?.error;
      showAlert(backendMessage || t('routeSettlement.recalculateError'), 'error');
      console.error('Error reliquidando:', error);
    } finally {
      hideLoading();
      setReliquidating(false);
    }
  };
  const handleRouteChange = (nextRouteId: string) => {
    setRouteId(nextRouteId);
    setCashBoxId(0);
    setActiveSearch(null);
    resetSearchData();

    if (!officeId || !nextRouteId) {
      return;
    }

    (async () => {
      const resolvedCashBoxId = await ensureCashBoxForRoute(nextRouteId);
      if (resolvedCashBoxId <= 0) {
        showAlert(t('routeSettlement.cashBoxResolveError'), 'error');
        setActiveSearch(null);
        resetSearchData();
        return;
      }
      mergeLiquidationSearchTarget(nextRouteId, currentDate);
    })();
  };
  const handleOpenModal = () => {
    if (!canRegisterExpenses) {
      showAlert(t('routeSettlement.noPermissionExpenses'), 'warning');
      return;
    }
    if (!hasActiveSearch) {
      showAlert(t('routeSettlement.searchBeforeExpenses'), 'warning');
      return;
    }
    setModalOpen(true);
  };
  const handleModalConfirm = () => {
    if (!canCreate) {
      showAlert(t('routeSettlement.noPermissionSettle'), 'warning');
      return;
    }
    setModalConfirm(true);
  };
  const CloseModalConfirm = () => setModalConfirm(false);
  const handleCloseModal = () => setModalOpen(false);
  const displayedExpenses = useMemo(() => {
    return listExpenses?.[0]?.map(exp => ({
      id: exp.id,
      name: exp.expenseType.name,
      value: exp.value,
      description: exp.description,
      cashBoxId,
      isLocal: false,
      expenseDate: exp.date,
    })) || [];
  }, [listExpenses, cashBoxId]);
  const effectiveTotalExpenses = useMemo(() => {
    return displayedExpenses.reduce(
      (sum, expense) => sum + Number(expense.value ?? 0),
      0
    );
  }, [displayedExpenses]);
  const dailyInsuranceIncome = Number(insuranceBoxSummary?.dateIncome ?? 0);
  const expectedCash = useMemo(() => {
    return paymentTotal + baseNumber + dailyInsuranceIncome - loanTotal - effectiveTotalExpenses;
  }, [baseNumber, paymentTotal, dailyInsuranceIncome, loanTotal, effectiveTotalExpenses]);
  const balance = useMemo(() => {
    return cashNumber - expectedCash;
  }, [expectedCash, cashNumber]);

  useEffect(() => {
    setCashDifference(balance)
  }, [balance]);

  const resetExpenses = async (createdExpenses?: CreatedExpenseDisplayItem[]) => {
    if (!hasActiveSearch || !activeRouteId || !activeDate) return;
    if (createdExpenses && createdExpenses.length > 0) {
      const newItems: ExpenseItem[] = createdExpenses.map(exp => ({
        id: exp.id,
        date: normalizeExpenseDate(activeDate),
        description: exp.description,
        value: exp.value,
        balance: 0,
        expenseType: {
          id: exp.expenseTypeId,
          name: exp.name,
          cashBoxAdjust: false,
        },
      }));

      setListExpenses(prev => {
        const currentItems = prev?.[0] || [];
        return [[...currentItems, ...newItems], currentItems.length + newItems.length];
      });
      if (cashBoxId) {
        try {
          const dayClosingRecord = await loadDayClosingByDate({
            routeId: activeRouteId,
            cashBoxId,
            date: activeDate,
          });
          applyDayClosingData(dayClosingRecord, { preserveManualFields: true });
          setDateWithoutMovements(
            liquidationDayHasNoMovements(dayClosingRecord, [
              [...(listExpenses?.[0] || []), ...newItems],
              (listExpenses?.[0]?.length || 0) + newItems.length,
            ]),
          );
        } catch (error) {
          console.error('Error refrescando closer tras registrar gastos:', error);
          setDateWithoutMovements(false);
        }
      } else {
        setDateWithoutMovements(false);
      }
      return;
    }

    showLoading();
    try {
      setListExpenses(null);
      if (cashBoxId) {
        const { data } = await axios.post(`/cash-box-expenses/search`, {
          routeId: activeRouteId,
          date: normalizeExpenseDate(activeDate),
        });
        setListExpenses(data);
        const dayClosingRecord = await loadDayClosingByDate({
          routeId: activeRouteId,
          cashBoxId,
          date: activeDate,
        });
        applyDayClosingData(dayClosingRecord, { preserveManualFields: true });
        setDateWithoutMovements(liquidationDayHasNoMovements(dayClosingRecord, data));
      }
    } catch (error) {
      showAlert(t('routeSettlement.reloadExpensesError'), 'error');
    } finally {
      hideLoading();
    }
  };

  const handleDeleteExpense = async (expenseId: number, expenseDateRaw?: string | null) => {
    if (!canDelete) {
      showAlert(t('routeSettlement.noPermissionDeleteExpense'), 'warning');
      return;
    }
    if (deletingExpenseId === expenseId) {
      return;
    }
    const previousListExpenses = listExpenses;
    try {
      showLoading();
      const currentRouteId = routeId || activeRouteId;
      const expenseRow = listExpenses?.[0]?.find(exp => Number(exp.id) === expenseId);
      const currentExpenseDate = expenseDateRaw || expenseRow?.date || currentDate || activeDate;
      const requestDate = currentExpenseDate ? normalizeExpenseDate(currentExpenseDate) : null;
      setDeletingExpenseId(expenseId);

      setListExpenses((prev) => {
        if (!prev) return prev;
        const updatedExpenses = prev[0].filter((exp) => Number(exp.id) !== expenseId);
        return [updatedExpenses, updatedExpenses.length];
      });

      await axios.delete(`/cash-box-expenses/${expenseId}`);

      let expensesTupleForFlag: [ExpenseItem[], number] | null = null;
      if (currentRouteId && requestDate) {
        const { data } = await axios.post(`/cash-box-expenses/search`, {
          routeId: currentRouteId,
          date: requestDate,
        });
        setListExpenses(data);
        expensesTupleForFlag = data;
      }

      if (currentRouteId && requestDate && cashBoxId) {
        const dayClosingRecord = await loadDayClosingByDate({
          routeId: currentRouteId,
          cashBoxId,
          date: requestDate,
        });
        applyDayClosingData(dayClosingRecord, { preserveManualFields: true });
        if (expensesTupleForFlag) {
          setDateWithoutMovements(liquidationDayHasNoMovements(dayClosingRecord, expensesTupleForFlag));
        }
      }

      showAlert(t('routeSettlement.expenseDeleted'), 'success');
    } catch (error) {
      setListExpenses(previousListExpenses);
      showAlert(t('routeSettlement.expenseDeleteError'), 'error');
      console.error('Error deleting expense:', error);
    } finally {
      setDeletingExpenseId(null);
      hideLoading();
    }
  };

  type ExpenseRow = (typeof displayedExpenses)[number];
  const expenseColumns: DataTableColumn<ExpenseRow>[] = [
    { id: 'name', header: t('routeSettlement.expenseName'), value: (r) => r.name, sortable: true },
    { id: 'value', header: t('routeSettlement.value'), value: (r) => Number(r.value ?? 0), render: (r) => formatCurrency(Number(r.value ?? 0)), align: 'right', sortable: true },
    { id: 'description', header: t('routeSettlement.description'), value: (r) => r.description || '', render: (r) => r.description || '—', wrap: true, hideBelow: 'md' },
  ];

  const submit = async (event: SyntheticEvent) => {
    event.preventDefault(); // Evita el envío del formulario por defecto
    if (listExpenses === null) {
      showAlert(t('routeSettlement.loadExpensesFirst'), 'warning');
      return;
    }
    const parsedBase = parseLocalizedNumber(base);
    const parsedCash = parseLocalizedNumber(cash);
    const parsedRouteId = Number(routeId);
    let parsedUserId = Number(userId);
    let parsedCashBoxId = Number(cashBoxId);

    if (parsedBase === null || parsedCash === null) {
      showAlert(t('routeSettlement.baseCashInvalid'), 'warning');
      return;
    }

    if (!Number.isInteger(parsedRouteId) || parsedRouteId <= 0) {
      showAlert(t('routeSettlement.selectValidRoute'), 'warning');
      return;
    }

    const currentTime = moment().tz(operationalTimezone()).format("HH:mm:ss");
    const fullcurrentDate = moment.tz(
      `${currentDate} ${currentTime}`,
      "YYYY-MM-DD HH:mm:ss",
      operationalTimezone()
    ).format("YYYY-MM-DD HH:mm:ss");
    const formData = {
      date: fullcurrentDate,
      base: parsedBase,
      cash: parsedCash,
      expectedCash,
      routePercentage,
      expenseTotal: effectiveTotalExpenses,
      discountTotal,
      otherIncomeTotal,
      otherExpenseTotal,
      paymentTotal,
      loanTotal,
      interestTotal,
      insuranceChargeTotal,
      refinanceValue,
      refinanceTotal,
      cancellationTotal: valuePaymentCancel,
      paymentCount,
      loanCount,
      cancellationCount: countLoanCancel,
      clientCount,
      cashBoxValue,
      defaultClientValue: valueClientDefault,
      defaultClientCount: countClientDefault,
      cashDifference,
      routeId: parsedRouteId,
      userId: parsedUserId,
      visitOrderId,
      cashBoxId: parsedCashBoxId,
      closed: 1,
    };
    const url = "/day-closings";
    try {
      showLoading();
      if (!Number.isInteger(parsedUserId) || parsedUserId <= 0) {
        const { data } = await axios.get('user');
        parsedUserId = Number(data?.data?.id || 0);
      }
      if (!Number.isInteger(parsedCashBoxId) || parsedCashBoxId <= 0) {
        const cashBoxResponse = await axios.get(`/routes/${parsedRouteId}/cash-box`);
        parsedCashBoxId = Number(cashBoxResponse.data?.data?.[0]?.id || 0);
      }
      if (![parsedUserId, parsedCashBoxId].every(id => Number.isInteger(id) && id > 0)) {
        showAlert(t('routeSettlement.userBoxResolveError'), 'warning');
        return;
      }

      const response = await axios.post(url, {
        ...formData,
        userId: parsedUserId,
        cashBoxId: parsedCashBoxId,
      });
      showAlert(t('routeSettlement.savedOk'), 'success');
      await new Promise(resolve => setTimeout(resolve, 1000));
      setModalConfirm(false)
      navigate("/processes/route-settlement");
    } catch (error) {
      const backendMessage = (error as any)?.response?.data?.message;
      showAlert(backendMessage || t('routeSettlement.saveError'), 'error');
      console.error('Error guardando liquidación:', error);
    } finally {
      hideLoading();
    }
  };

  const money = { currency: companyCurrency(), locale: companyLocale() };
  const summaryRows: { label: string; value: React.ReactNode; strong?: boolean }[] = [
    { label: t('routeSettlement.paymentsCount'), value: paymentCount },
    { label: t('routeSettlement.paymentsValue'), value: formatCurrency(paymentTotal) },
    { label: t('routeSettlement.loansCount'), value: loanCount },
    { label: t('routeSettlement.loansValue'), value: formatCurrency(loanTotal) },
    { label: t('routeSettlement.payoffs'), value: countLoanCancel },
    { label: t('routeSettlement.payoffsValue'), value: formatCurrency(valueLoanCancel) },
    { label: t('routeSettlement.discounts'), value: formatCurrency(discountTotal) },
    { label: t('routeSettlement.managementFees'), value: formatCurrency(insuranceChargeTotal) },
    { label: t('routeSettlement.loanInsuranceIncome'), value: formatCurrency(dailyInsuranceIncome) },
    { label: t('routeSettlement.expenses'), value: formatCurrency(effectiveTotalExpenses) },
    { label: t('routeSettlement.clientsWithLoan'), value: clientCount },
    { label: t('routeSettlement.collectionPercent'), value: `${routePercentage}%` },
    { label: t('routeSettlement.defaultClients'), value: `${countClientDefault} · ${formatCurrency(valueClientDefault)}` },
    { label: t('routeSettlement.expectedCash'), value: formatCurrency(expectedCash), strong: true },
    { label: t('routeSettlement.cashDifference'), value: formatCurrency(balance), strong: true },
  ];

  return (
    <HeaderResponsive>
      <PageHeader
        title={t('routeSettlement.title')}
        description={t('routeSettlement.subtitle')}
        actions={
          <>
            <PermissionButton
              allowed={canUpdate}
              variant="outlined"
              deniedTooltip={t('routeSettlement.noPermissionRecalculate')}
              startIcon={reliquidating ? <CircularProgress size={18} color="inherit" /> : <AutorenewOutlinedIcon />}
              onClick={handleReliquidar}
              disabled={!canOperateWithCashBox || reliquidating}
            >
              {t('routeSettlement.recalculate')}
            </PermissionButton>
            <PermissionButton
              allowed={canCreate}
              variant="contained"
              deniedTooltip={t('routeSettlement.noPermissionSettle')}
              startIcon={<SaveOutlinedIcon />}
              onClick={handleModalConfirm}
              disabled={!hasActiveSearch}
            >
              {t('routeSettlement.settle')}
            </PermissionButton>
          </>
        }
      />

      <FilterBar>
        <SelectField
          id="settlement-office"
          label={t('common.selectOffice')}
          value={officeId}
          options={offices}
          onChange={(v) => setOfficeId(v)}
        />
        <SelectField
          id="settlement-route"
          label={t('common.selectRoute')}
          value={routeId}
          options={officeRoutes}
          onChange={(v) => handleRouteChange(v)}
          disabled={!officeId}
        />
        <DateField
          id="settlement-date"
          label={t('common.date')}
          value={currentDate}
          onChange={(v) => v && setCurrentDate(v)}
          fullWidth
        />
      </FilterBar>

      {dateWithoutMovements && hasActiveSearch && (
        <Box sx={{ mb: 2 }}>
          <Notice severity="warning">{t('routeSettlement.noMovements')}</Notice>
        </Box>
      )}

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          mb: 2,
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(130px, 100%), 1fr))',
        }}
      >
        <StatCard label={t('routeSettlement.statPayments')} value={formatCurrency(paymentTotal)} hint={t('routeSettlement.statPaymentsHint', { count: paymentCount })} />
        <StatCard label={t('routeSettlement.statLoans')} value={formatCurrency(loanTotal)} hint={t('routeSettlement.statLoansHint', { count: loanCount })} />
        <StatCard label={t('routeSettlement.statExpenses')} value={formatCurrency(effectiveTotalExpenses)} />
        <StatCard label={t('routeSettlement.statExpectedCash')} value={formatCurrency(expectedCash)} />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'repeat(2, minmax(0, 1fr))', xl: 'minmax(0, 3fr) minmax(0, 2fr)' },
          alignItems: 'start',
        }}
      >
        <Section
          title={t('routeSettlement.expensesTitle')}
          action={
            <PermissionButton
              allowed={canRegisterExpenses}
              variant="outlined"
              deniedTooltip={t('routeSettlement.noPermissionExpenses')}
              startIcon={<AddOutlinedIcon />}
              onClick={handleOpenModal}
              disabled={!hasActiveSearch}
            >
              {t('routeSettlement.registerExpense')}
            </PermissionButton>
          }
        >
          <DataTable<ExpenseRow>
            rows={displayedExpenses}
            columns={expenseColumns}
            getRowId={(r) => r.id}
            ariaLabel={t('routeSettlement.expensesTitle')}
            maxHeight="min(50vh, 480px)"
            pageSizeOptions={[10, 20, 50]}
            rowActions={(r) => {
              const expenseId = Number(r.id);
              if (Number.isNaN(expenseId)) return null;
              return (
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                  <PhotoViewerButton ownerType="expense" ownerId={expenseId} title={String(r.name || '')} />
                  {canDelete ? (
                    <Tooltip title={t('common.delete')}>
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          aria-label={`${t('common.delete')} ${r.name}`}
                          disabled={deletingExpenseId === expenseId}
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpenseToDelete({ id: expenseId, name: String(r.name || ''), date: r.expenseDate });
                          }}
                        >
                          {deletingExpenseId === expenseId ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineOutlinedIcon fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  ) : null}
                </Box>
              );
            }}
            emptyState={
              <EmptyState
                dense
                icon={<ReceiptLongOutlinedIcon />}
                title={t('routeSettlement.noExpenses')}
                description={hasActiveSearch ? undefined : t('routeSettlement.selectToStart')}
              />
            }
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
            <Typography variant="subtitle2">{t('routeSettlement.expensesTotal', { value: formatCurrency(effectiveTotalExpenses) })}</Typography>
          </Box>
        </Section>

        <Section title={t('routeSettlement.tableTitle')}>
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', mb: 2 }}>
            <MoneyField
              id="settlement-base"
              label={t('routeSettlement.base')}
              value={baseNumber}
              onChange={(v) => setBase(String(v ?? 0))}
              currency={money.currency}
              locale={money.locale}
              fullWidth
            />
            <MoneyField
              id="settlement-cash"
              label={t('routeSettlement.cash')}
              value={cashNumber}
              onChange={(v) => setCash(String(v ?? 0))}
              currency={money.currency}
              locale={money.locale}
              fullWidth
            />
          </Box>
          <Box
            component="dl"
            aria-label={t('routeSettlement.tableAria')}
            sx={{
              m: 0,
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) auto',
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              overflow: 'hidden',
              '& > dt, & > dd': { m: 0, px: 1.5, py: 1, borderTop: 1, borderColor: 'divider', typography: 'body2' },
              '& > dt:first-of-type, & > dt:first-of-type + dd': { borderTop: 0 },
              '& > dt': { color: 'text.secondary' },
              '& > dd': { textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
              '& > .strong': { fontWeight: 700, color: 'text.primary', bgcolor: 'action.hover' },
            }}
          >
            {summaryRows.map((row) => (
              <React.Fragment key={row.label}>
                <dt className={row.strong ? 'strong' : undefined}>{row.label}</dt>
                <dd className={row.strong ? 'strong' : undefined}>{row.value}</dd>
              </React.Fragment>
            ))}
          </Box>
        </Section>
      </Box>

      <RouteExpensesModal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        routeId={routeId}
        cashBoxId={Number(cashBoxId)}
        userId={userId}
        selectedDate={currentDate}
        showLoading={showLoading}
        hideLoading={hideLoading}
        canSave={canRegisterExpenses}
        onAfterSubmit={resetExpenses}
      />
      <ConfirmDayClosingModal
        isOpen={modalOpenConfirm}
        onClose={CloseModalConfirm}
        onConfirm={submit}
      />
      <ConfirmDialog
        open={confirmRecalculate}
        variant="warning"
        title={t('routeSettlement.confirmRecalculateTitle')}
        message={t('routeSettlement.confirmRecalculate', { date: normalizeExpenseDate(currentDate) })}
        confirmLabel={t('routeSettlement.recalculate')}
        cancelLabel={t('common.cancel')}
        onConfirm={() => void runReliquidar()}
        onCancel={() => setConfirmRecalculate(false)}
      />
      <ConfirmDialog
        open={!!expenseToDelete}
        variant="danger"
        title={t('routeSettlement.confirmDeleteExpenseTitle')}
        message={expenseToDelete ? t('routeSettlement.confirmDeleteExpense', { name: expenseToDelete.name }) : ''}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        onConfirm={() => {
          const target = expenseToDelete;
          setExpenseToDelete(null);
          if (target) void handleDeleteExpense(target.id, target.date);
        }}
        onCancel={() => setExpenseToDelete(null)}
      />
    </HeaderResponsive>
  );
};

export default RouteSettlement;
